/**
 * @prettier
 */

import React, { useState, useCallback, useMemo } from 'react';
import _set from 'lodash/set';
import _get from 'lodash/get';
import { Link } from '@zola/zola-ui/src/components/Link';
import { CARD_TYPES_SINGULAR } from 'cards/constants/Copy';
import FeatureFlags from '../../../../../util/featureFlags';
import BreadCrumbs from '../BreadCrumbs/BreadCrumbs';
import SamplePreview from '../SamplePreview/SamplePreview';
import Layout from '../Layout';
import { useDebounce } from '../util/hooks';
import styles from './styles.module.less';
import { STEPS } from '../constants/steps';

const WeddingDetailsForm = ({
  form,
  goToStep,
  errors,
  step,
  breadcrumbSteps,
  isGuest,
  cardType,
  colors,
  isLetterpress,
  isStampedFoil,
  template,
  updateForm,
  openModal,
  closeModal,
  fetchUserContext,
  sampleNotCustomizable,
  skuData,
  cardSuiteDetails,
  isMagnet,
  isMulti,
  setMultiPreviewState,
  multiPreviewState,
  cardSuiteUUIDList,
  cardSuiteDetailsSet,
  multiHasCustomizableCard,
}) => {
  const [localForm, setLocalForm] = useState({ ...form.weddingDetails });

  const setField = useCallback((key, value) => setLocalForm({ ...localForm, [key]: value }), [
    localForm,
    setLocalForm,
  ]);

  // Once the local form has stopped being updated for 1 sec, update parent form. We never save invalid dates.
  useDebounce(
    () => {
      updateForm({ weddingDetails: { ...localForm, date: localForm.date } });
    },
    1000,
    [localForm]
  );

  const savedForm = useMemo(() => _set(form, ['account', 'password'], null), [form]);

  const onSuccess = useCallback(() => {
    fetchUserContext().then(() => {
      openModal({ initialForm: savedForm });
    });
  }, [savedForm, fetchUserContext, openModal]);

  const onLogin = useCallback(() => {
    closeModal({ shouldNotDisableMulti: true });

    setTimeout(() => {
      window.zolaNav.openLoginModal({
        loginCallback: onSuccess,
        signupLinkCallback: onSuccess,
        closeCallback: onSuccess,
      });
    }, 50);
  }, [onSuccess, closeModal]);

  const { fullName, partnerFullName, date } = localForm;
  const isPhotoValid = form.customizations.photo.valid;

  const userInvalid = _get(errors, ['weddingDetails', 'user', 'isInvalid'])
    ? _get(errors, ['weddingDetails', 'user', 'errorMessage'])
    : null;

  const partnerInvalid = _get(errors, ['weddingDetails', 'partner', 'isInvalid'])
    ? _get(errors, ['weddingDetails', 'partner', 'errorMessage'])
    : null;

  const fullNameInvalid = _get(errors, ['weddingDetails', 'field', 'fullName']) || null;

  const partnerFullNameInvalid =
    _get(errors, ['weddingDetails', 'field', 'partnerFullName']) || null;

  let layoutTextContent = (
    <>We&apos;ll print everything on your {CARD_TYPES_SINGULAR[cardType] || ''} sample for you.</>
  );
  if (isLetterpress) {
    layoutTextContent = (
      <>
        Letterpress samples are printed with default text. We&apos;ll save your details, so you can
        easily add them to your cards later.
      </>
    );
  }
  if (isStampedFoil) {
    layoutTextContent = (
      <>
        Foil design samples are printed with default text. We&apos;ll save your details, so you can
        easily add them to your cards later.
      </>
    );
  }
  if (isMagnet) {
    layoutTextContent = (
      <>
        Magnet samples are printed with default text. We&apos;ll save your details, so you can
        easily add them to your cards later.
      </>
    );
  }

  const setFullName = useCallback(e => setField('fullName', e.target.value), [setField]);
  const setPartnerName = useCallback(e => setField('partnerFullName', e.target.value), [setField]);
  const setDate = useCallback(value => setField('date', value), [setField]);

  return (
    <Layout.Form onSubmit={() => goToStep(STEPS.SHIPPING_DETAILS_FORM)}>
      <Layout.Box>
        <Layout.Row>
          <Layout.PreviewBox>
            <SamplePreview
              hideColorPicker={sampleNotCustomizable}
              colors={colors}
              template={template}
              form={form}
              updateForm={updateForm}
              sampleNotCustomizable={sampleNotCustomizable}
              skuData={skuData}
              cardSuiteDetails={cardSuiteDetails}
              isMagnet={isMagnet}
              isMulti={isMulti}
              setMultiPreviewState={setMultiPreviewState}
              multiPreviewState={multiPreviewState}
              cardSuiteUUIDList={cardSuiteUUIDList}
              cardSuiteDetailsSet={cardSuiteDetailsSet}
            />
          </Layout.PreviewBox>
          <Layout.Col>
            <BreadCrumbs.Text step={step} breadcrumbSteps={breadcrumbSteps} />
            <Layout.Header>Share a few wedding details</Layout.Header>
            <Layout.Text>{layoutTextContent}</Layout.Text>
            {isGuest && (
              <div className={styles.loginTextBox}>
                Already have an account?{' '}
                <Link role="button" onClick={onLogin}>
                  Log in
                </Link>
                .
              </div>
            )}
            <Layout.Field
              isRequired
              type="text"
              label="Your Full Name"
              disabled={!isGuest}
              name="fullName"
              value={fullName || ''}
              errorMessage={userInvalid || fullNameInvalid}
              onChange={setFullName}
            />
            <Layout.Field
              isRequired
              type="text"
              label="Your Partner's Full Name"
              disabled={!isGuest}
              name="partnerFullName"
              value={partnerFullName || ''}
              errorMessage={partnerInvalid || partnerFullNameInvalid}
              onChange={setPartnerName}
            />
            <Layout.DateField
              label="Wedding Date"
              inputName="date"
              value={date}
              onChange={setDate}
            />
            <Layout.FormButtons
              backLabel="Back"
              nextLabel="Next"
              backDisabled={breadcrumbSteps.findIndex(k => k === step.key) === 0}
              nextDisabled={
                !(fullName && form.weddingDetails.fullName === fullName) ||
                !(partnerFullName && form.weddingDetails.partnerFullName === partnerFullName)
              }
              onBack={() => {
                if (isMulti) {
                  if (multiHasCustomizableCard) {
                    goToStep(STEPS.CUSTOMIZATION);
                  } else {
                    goToStep(STEPS.MULTI_UPLOAD_HELP);
                  }
                } else if (
                  FeatureFlags.get('invitesSingleSampleCustomizeStep') &&
                  !sampleNotCustomizable
                ) {
                  goToStep(STEPS.CUSTOMIZATION);
                } else {
                  goToStep(isPhotoValid ? STEPS.PHOTO_CROP : STEPS.FILE_UPLOAD);
                }
              }}
            />
          </Layout.Col>
        </Layout.Row>
        <BreadCrumbs.Dots step={step} breadcrumbSteps={breadcrumbSteps} />
      </Layout.Box>
    </Layout.Form>
  );
};

export default WeddingDetailsForm;
